'use client';

import { useEffect, useRef } from 'react';
import { useRive, useViewModelInstanceBoolean } from '@rive-app/react-webgl2';
import { clsx } from 'clsx';

type RiveOnScrollAnimateProps = {
  src: string;
  stateMachine: string;
  artboard: string;
  instanceBoolean: string;
  className?: string;
};

export default function RiveOnScrollAnimate({
  src,
  stateMachine,
  artboard,
  instanceBoolean,
  className,
}: RiveOnScrollAnimateProps) {
  const revRef = useRef(null);

  const { rive, RiveComponent } = useRive({
    src,
    stateMachines: stateMachine,
    artboard,
    autoplay: true,
    autoBind: true,
  });

  const viewModelInstance = rive?.viewModelInstance;

  const { setValue: setIsInViewport } = useViewModelInstanceBoolean(instanceBoolean, viewModelInstance);

  useEffect(() => {
    const node = revRef.current;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsInViewport(entry.isIntersecting);
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px 0px 0px',
      },
    );

    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
      observer.disconnect();
    };
  }, [setIsInViewport]);

  return (
    <div ref={revRef} className={clsx('aspect-[635/500] h-full w-fit min-w-0 max-w-full overflow-hidden', className)}>
      <RiveComponent className="pointer-events-none block h-full w-full" />
    </div>
  );
}
